import type { AppState } from '../types'
import { persistentClients } from './clientPersistence'

const DB_NAME = 'worksmarter'
const DB_VERSION = 1
const STORE_NAME = 'application'
const STATE_KEY = 'current-state'

const emptyState: AppState = {
  schemaVersion: 1,
  clients: [],
  lastSavedAt: null,
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export async function loadState(): Promise<AppState> {
  const database = await openDatabase()
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readonly')
    const request = transaction.objectStore(STORE_NAME).get(STATE_KEY)
    request.onsuccess = () => {
      const saved = request.result as AppState | undefined
      if (saved?.schemaVersion !== 1) {
        resolve(emptyState)
        return
      }
      resolve({ ...saved, clients: persistentClients(saved.clients) })
    }
    request.onerror = () => reject(request.error)
    transaction.oncomplete = () => database.close()
  })
}

export async function saveState(state: AppState): Promise<void> {
  const database = await openDatabase()
  const persistentState: AppState = {
    ...state,
    clients: persistentClients(state.clients),
  }
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    transaction.objectStore(STORE_NAME).put(persistentState, STATE_KEY)
    transaction.oncomplete = () => {
      database.close()
      resolve()
    }
    transaction.onerror = () => reject(transaction.error)
  })
}

export async function replaceState(state: AppState): Promise<void> {
  await saveState(state)
}

export async function clearState(): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
    request.onblocked = () => reject(new Error('Локальная база занята другой вкладкой.'))
  })
}

export function createEmptyState(): AppState {
  return structuredClone(emptyState)
}
